import { getDuplicateValueKey, isCaseInsensitiveDuplicateKind } from 'components/spreadsheet/helpers/duplicates'
import { getRowValue } from 'components/spreadsheet/helpers/functions'
import { checkValuesSame } from 'components/spreadsheet/helpers/paste'
import { isValidValue } from 'components/spreadsheet/helpers/validation'
import { ISpreadsheetData } from 'components/spreadsheet/types'
import { ICellValue, ITableViewColumn } from 'types'

/** A problem with the page's own request, safe to report back to the page verbatim. */
export class BridgeError extends Error {}

/** The grid's message with the column it's about in front of it, unless the message already says which one. */
const namingColumn = (columnName: string, message: string): string =>
  message.includes(columnName) ? message : `"${columnName}": ${message}`

/**
 * How often each no-duplicates column holds each value while one request is checked: the grid's counts, changed by
 * the rows checked before, so two rows of one request can't both save a value the column allows only once.
 */
export type WriteTally = Map<string, Record<string, number>>

const countsFor = (tally: WriteTally, column: ITableViewColumn, data: ISpreadsheetData): Record<string, number> => {
  let counts = tally.get(column.publicId)
  if (!counts) {
    counts = { ...data.columnValuesCount[column.publicId] }
    tally.set(column.publicId, counts)
  }
  return counts
}

// As isValidValue counts a value: JSON columns aren't checked for duplicates, and email columns ignore case.
const countKey = (column: ITableViewColumn, value: ICellValue): string | undefined =>
  value === null || value === undefined
    ? undefined
    : getDuplicateValueKey(value.toString(), isCaseInsensitiveDuplicateKind(column.kind))

/**
 * Runs the grid's column validation over the values a page is saving, as a cell edit would, and only for the columns
 * it supplies and, on an edit, only the ones that change what the row holds (a page can send a row's whole record back
 * with one field changed). A value the column blocks throws with the grid's own message, naming the column; one
 * it only warns about is returned so the page can show it after the save. Pass rowId for an edit, none for a new row,
 * and the same tally for every row of one request.
 */
export function validateWrite(
  values: Record<string, ICellValue>,
  data: ISpreadsheetData,
  rowId?: string,
  tally: WriteTally = new Map()
): string[] {
  const row = rowId === undefined ? undefined : data.rows.find((candidate) => candidate.publicId === rowId)
  // Select options can depend on other columns, so check against the row as it will be: what it holds, then the page's values.
  const existing = Object.fromEntries(
    data.viewDetails.columns.map((column) => [
      column.publicId,
      row ? getRowValue(row, column.publicId, data.columnIdToIndex, data.columnStore) : null
    ])
  )
  const rowData = { ...existing, ...values }
  const warnings: string[] = []
  for (const [columnId, value] of Object.entries(values)) {
    const column = data.viewDetails.columns.find((candidate) => candidate.publicId === columnId)
    if (!column) continue
    // As in the grid, a cell that isn't changed isn't checked: its value was already there, and it would count as its own duplicate.
    if (row && checkValuesSame(existing[columnId], value, column.kind)) continue
    const counts = column.validationNoDuplicates && column.kind !== 'json' ? countsFor(tally, column, data) : undefined
    const seen = counts ? { ...data.columnValuesCount, [column.publicId]: counts } : data.columnValuesCount
    // As in a grid edit, the value isn't counted yet.
    const result = isValidValue(value, rowData, seen, column, undefined, undefined, true)
    if (counts) {
      // Rows after this one see it saved: its new value counted, and the value an edit replaces no longer.
      const added = countKey(column, value)
      const removed = row ? countKey(column, existing[columnId]) : undefined
      if (added !== undefined) counts[added] = (counts[added] ?? 0) + 1
      if (removed !== undefined && counts[removed]) counts[removed] = counts[removed] - 1
    }
    if (!result.error) continue
    if (!result.warn) throw new BridgeError(namingColumn(column.name, result.errorMessage))
    warnings.push(result.errorMessage)
  }
  return warnings
}
